import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import LanguageIcon from "@mui/icons-material/Language";
import { getSpotlightData, getApplicationData } from "../../services/api";
import bg from "../../../assets/images/bg.png";

function HomeScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDarkMode = useSelector((state) => state.selections.isDarkMode);

  // Curated Boards API state
  const [curatedBoards, setCuratedBoards] = useState([]);
  const [curatedBoardsLoading, setCuratedBoardsLoading] = useState(false);
  const [curatedBoardsError, setCuratedBoardsError] = useState("");

  // Spotlight API state
  const [spotlightItems, setSpotlightItems] = useState([]);
  const [spotlightLoading, setSpotlightLoading] = useState(false);
  const [spotlightError, setSpotlightError] = useState("");

  useEffect(() => {
    const fetchCuratedBoardsData = async () => {
      try {
        setCuratedBoardsLoading(true);
        setCuratedBoardsError("");
        const response = await getApplicationData({
          payload: { appType: ["GROUP_REPORT"], groupby: "Data Product" },
        });
        const apiData = response?.data?.data?.groupByData || [];
        const mappedBoards = apiData.flatMap((group) =>
          group.data.map((item) => {
            const owners = Array.isArray(item?.owner)
              ? item.owner
              : Array.isArray(item?.owned_by)
              ? item.owned_by
              : [];
            const primaryOwner = owners?.[0]?.name
              ? formatedName(owners[0].name)
              : "Unknown Owner";
            const extraCount = Math.max((owners?.length || 0) - 1, 0);
            return {
              appId: item.appId || item.id || item.app_id,
              title: item.name || item.app_name,
              subtitle: primaryOwner,
              ownersCount: owners?.length || 0,
              extraOwners: extraCount, // number of owners beyond the primary
              region: group.groupByValue,
            };
          })
        );
        setCuratedBoards(mappedBoards);
      } catch (error) {
        setCuratedBoardsError("Failed to load curated boards");
        setCuratedBoards([]);
      } finally {
        setCuratedBoardsLoading(false);
      }
    };

    // Utility: format owner names nicely - remove parentheses and format properly
    const formatedName = (userName = "") => {
      try {
        // Remove anything in parentheses like "(Contractor)" and clean up spaces
        let cleaned = userName.replace(/\s*\([^)]*\)\s*/g, "").trim();

        // Split by comma if present (format: "LastName, FirstName")
        const parts = cleaned.split(",");

        if (parts.length === 2) {
          // Reorder from "LastName, FirstName" to "FirstName LastName"
          const lastName = parts[0].trim();
          const firstName = parts[1].trim();
          return `${firstName} ${lastName}`;
        }

        // If no comma, return as-is (already in correct format)
        return cleaned;
      } catch (e) {
        return userName || "";
      }
    };

    const fetchSpotlight = async () => {
      try {
        setSpotlightLoading(true);
        setSpotlightError("");
        const response = await getSpotlightData();
        const apiData = response?.data?.data || [];
        const mapped = apiData.map((item) => {
          const owners = Array.isArray(item?.owners) ? item.owners : [];
          const primaryOwner = owners?.[0]?.name
            ? formatedName(owners[0].name)
            : "Unknown";
          const extraCount = Math.max((owners?.length || 0) - 1, 0);
          return {
            title: item?.name || "",
            subtitle: primaryOwner,
            ownersCount: owners?.length || 0,
            extraOwners: extraCount, // number of owners beyond the primary
            description: item?.desc || "",
            endpoint: item?.endpoint || "/discover",
          };
        });
        setSpotlightItems(mapped);
      } catch (err) {
        setSpotlightError(
          err?.error || err?.message || "Failed to load spotlight data"
        );
      } finally {
        setSpotlightLoading(false);
      }
    };

    fetchCuratedBoardsData();
    fetchSpotlight();
  }, []);

  // Local presentational component for Curated Board/Placeholder tiles
  const CuratedBoardTile = ({ board, region, containerSx }) => {
    if (!board) return null;
    return (
      <Box
        onClick={() => {
          if (board?.appId) {
            navigate(`/discover/${encodeURIComponent(board.appId)}`);
          } else {
            navigate(`/discover?region=${encodeURIComponent(region)}`);
          }
        }}
        title={`Click to view ${board.title} data in ${region} region`}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          alignSelf: "start",
          padding: "16px",
          gap: "8px",
          minHeight: 72,
          background:
            isDarkMode === "dark"
              ? theme.palette.primary.lightBackground
              : "#F3F5F8",
          borderRadius: "10px",
          cursor: "pointer",
          transition: "background-color 0.2s ease, box-shadow 0.2s ease",
          border: `1px solid ${theme.palette.tertiary?.lightGrey || "#E0E0E0"}`,
          "&:hover": {
            background:
              isDarkMode === "dark" ? theme.palette.cards.hover : "#FFFFFF",
            border: "1px solid #008AAF",
            boxShadow: "0 4px 12px rgba(0, 192, 243, 0.2)",
          },
          ...containerSx,
        }}
      >
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <LanguageIcon
              sx={{
                width: "16px",
                height: "16px",
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color:
                  theme.palette.cards?.heading || theme.palette.text.primary,
                fontSize: "14px",
                lineHeight: "120%",
              }}
            >
              {board.title}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src="/person.svg"
              alt="Owner"
              style={{
                width: 16,
                height: 16,
                objectFit: "contain",
                filter:
                  isDarkMode === "dark" ? "brightness(0) invert(1)" : "none",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette.cards?.subheading ||
                  theme.palette.text.secondary,
                fontSize: "12px",
                lineHeight: "120%",
              }}
            >
              Owner: {board.subtitle}
              {board?.extraOwners > 0 ? ` +${board.extraOwners}` : ""}
            </Typography>
          </Box>
        </>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: theme.palette.primary.backgroundColor,
        height: "calc(100vh - 56px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header image area */}
      <Box
        sx={{
          height: "70%",
          backgroundColor: "transparent",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <img
          src="/rectangle542.png"
          alt="Header background"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "top",
            display: "block",
          }}
        />
        {/* Bottom-only gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "50%",
            background:
              isDarkMode === "dark"
                ? "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%)"
                : "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)",
            pointerEvents: "none",
          }}
        />
        {/* Left-side hero text overlay (from Home.jsx) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "flex-start",
            pl: "24px",
            pr: "84px",
          }}
        >
          <Box sx={{ width: "30%", pt: 4, pr: 4, pb: 0, pl: 0, mt: 3 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color:
                  isDarkMode === "dark"
                    ? theme.palette.primary.darkViolet
                    : "#1D387E",
                fontSize: "3rem",
                mb: 1,
                lineHeight: 1.1,
              }}
            >
              DART
            </Typography>
            <Typography
              sx={{
                width: "348px",
                height: "35px",
                fontFamily: "'Noto Sans'",
                fontStyle: "normal",
                fontWeight: 700,
                fontSize: "29px",
                lineHeight: "120%",
                letterSpacing: "0%",
                display: "flex",
                alignItems: "center",
                color:
                  isDarkMode === "dark"
                    ? theme.palette.primary.darkViolet
                    : "#1D387E",
                mb: 1,
              }}
            >
              All your Data. One Place
            </Typography>

            <Button
              variant="contained"
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                padding: "10px 16px",
                gap: "8px",
                width: "92px",
                height: "37px",
                background:
                  isDarkMode === "dark"
                    ? theme.palette.secondary.main
                    : "#1D387E",
                borderRadius: "100px",
                textTransform: "none",
                color: "#FFFFFF",
                fontFamily: "'Noto Sans'",
                fontWeight: 700,
                fontSize: "14px",
                top: "50px",
                // lineHeight: "120%",
                "&:hover": {
                  background:
                    isDarkMode === "dark"
                      ? theme.palette.secondary.main
                      : "#1D387E",
                },
              }}
              onClick={() => navigate("/discover")}
            >
              Discover
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Body: 1x3 grid using original card styling */}
      <Box
        sx={{
          height: "30%",
          display: "grid",
          gridTemplateColumns: "4.5fr 3fr 4.5fr",
          gap: "24px",
          zIndex: 1000,
          padding: "0 24px",
        }}
      >
        {/* Left Column - Curated Boards */}
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "24px 22px",
            marginTop: "-60px",
            gap: "18px",
            background:
              isDarkMode === "dark"
                ? theme.palette.cards.background
                : "#FFFFFF",
            border: `1px solid ${
              theme.palette.tertiary?.lightGrey || "#E0E0E0"
            }`,
            boxShadow: "0px 6px 6px rgba(0, 0, 0, 0.06)",
            borderRadius: "50px 0px",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color:
                  isDarkMode === "dark"
                    ? theme.palette.primary.darkViolet
                    : "#1D387E",
                fontSize: "18px",
                lineHeight: "120%",
              }}
            >
              Curated Boards
            </Typography>
            <Button
              variant="text"
              disableRipple
              disableFocusRipple
              sx={{
                display: "inline-flex",
                alignItems: "center",
                color:
                  isDarkMode === "dark"
                    ? theme.palette.primary.darkViolet
                    : "#1D387E",
                textTransform: "none",
                "&:hover": {
                  textDecoration: "underline",
                  backgroundColor: "transparent !important",
                },
                "&:active": {
                  textDecoration: "underline",
                  backgroundColor: "transparent !important",
                },
                "&:focus": {
                  textDecoration: "underline",
                  backgroundColor: "transparent !important",
                },
                gap: "6px",
                p: 0,
                minWidth: 0,
              }}
              onClick={() => navigate("/discover")}
            >
              <Typography
                sx={{
                  fontFamily: "'Noto Sans'",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "120%",
                  color:
                    isDarkMode === "dark"
                      ? theme.palette.primary.darkViolet
                      : "#1D387E",
                }}
              >
                View all
              </Typography>
              <NorthEastIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  color:
                    isDarkMode === "dark"
                      ? theme.palette.primary.darkViolet
                      : "#1D387E",
                }}
              />
            </Button>
          </Box>

          {/* Content */}
          <Box
            sx={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: "12px",
            }}
          >
            {curatedBoardsLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flex: 1,
                }}
              >
                <CircularProgress
                  size={24}
                  sx={{
                    color:
                      isDarkMode === "dark"
                        ? theme.palette.primary.darkViolet
                        : "#1D387E",
                  }}
                />
              </Box>
            ) : curatedBoardsError ? (
              <Alert severity="error" sx={{ width: "100%" }}>
                {curatedBoardsError}
              </Alert>
            ) : curatedBoards.length === 0 ? (
              <Typography color="text.secondary">
                No curated boards available
              </Typography>
            ) : (
              <>
                {/* Three rows of two each */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "12px",
                    alignItems: "start",
                  }}
                >
                  {curatedBoards.slice(0, 2).map((board, idx) => (
                    <CuratedBoardTile
                      key={idx}
                      board={board}
                      region={board.region}
                    />
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "12px",
                    alignItems: "start",
                  }}
                >
                  {curatedBoards.slice(2, 4).map((board, idx) => (
                    <CuratedBoardTile
                      key={idx + 2}
                      board={board}
                      region={board.region}
                    />
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: "12px",
                    alignItems: "start",
                  }}
                >
                  {curatedBoards.slice(4, 6).map((board, idx) => (
                    <CuratedBoardTile
                      key={idx + 4}
                      board={board}
                      region={board.region}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Middle Column - In Spotlight This Month */}
        <Box
          sx={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "24px 22px",
            marginTop: "-60px",
            gap: "18px",
            position: "relative",
            background:
              isDarkMode === "dark"
                ? theme.palette.cards?.background ||
                  theme.palette.background.paper
                : "#FFFCF6",
            border: `1px solid ${
              theme.palette.tertiary?.lightGrey || "#E0E0E0"
            }`,
            boxShadow: "0px 6px 6px rgba(0, 0, 0, 0.06)",
            borderRadius: "50px 0px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "5px",
              right: "5px",
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            <img
              src="/lamp-image.png"
              alt="Spotlight lamp"
              style={{
                width: "131px",
                height: "139px",
                objectFit: "contain",
                opacity: 1,
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Noto Sans'",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "120%",
              color:
                isDarkMode === "dark"
                  ? theme.palette.primary.darkViolet
                  : "#1D387E",
              zIndex: 2,
            }}
          >
            In Spotlight This Month
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              zIndex: 2,
            }}
          >
            {spotlightLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "120px",
                  width: "100%",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : spotlightError ? (
              <Alert severity="error" sx={{ width: "100%", fontSize: "12px" }}>
                {spotlightError}
              </Alert>
            ) : spotlightItems.length === 0 ? (
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "12px",
                  textAlign: "center",
                  width: "100%",
                  mt: 2,
                }}
              >
                No spotlight items available
              </Typography>
            ) : (
              spotlightItems.slice(0, 3).map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    mt: index === 1 ? "12px" : 0,
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <LanguageIcon
                      sx={{
                        width: "16px",
                        height: "16px",
                        color:
                          isDarkMode === "dark"
                            ? theme.palette.primary.darkViolet
                            : "#1D387E",
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "'Noto Sans'",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "120%",
                        color:
                          isDarkMode === "dark"
                            ? theme.palette.primary.darkViolet
                            : "#1D387E",
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        ml: "auto",
                      }}
                    >
                      <img
                        src="/person.svg"
                        alt="Owner"
                        style={{
                          width: 16,
                          height: 16,
                          objectFit: "contain",
                          filter:
                            isDarkMode === "dark"
                              ? "brightness(0) invert(1)"
                              : "none",
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "'Noto Sans'",
                          fontWeight: 400,
                          fontSize: "12px",
                          lineHeight: "120%",
                          color:
                            isDarkMode === "dark"
                              ? theme.palette.primary.darkViolet
                              : "#1D387E",
                        }}
                      >
                        {item.subtitle}
                        {item?.extraOwners > 0 ? ` +${item.extraOwners}` : ""}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "'Noto Sans'",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: 1.2,
                      color:
                        isDarkMode === "dark"
                          ? theme.palette.primary.darkViolet
                          : "#666666",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "10px 16px",
                      gap: "8px",
                      width: "fit-content",
                      height: "28px",
                      background:
                        isDarkMode === "dark"
                          ? theme.palette.secondary.main
                          : "#EFA362",
                      borderRadius: "100px",
                      textTransform: "none",
                      "&:hover": {
                        background:
                          isDarkMode === "dark"
                            ? theme.palette.secondary.main
                            : "#EFA362",
                      },
                    }}
                    onClick={() => navigate(item.endpoint || "/discover")}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Noto Sans'",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "120%",
                        color: "#FFFFFF",
                      }}
                    >
                      Explore
                    </Typography>
                    <NorthEastIcon
                      sx={{ width: 16, height: 16, color: "white" }}
                    />
                  </Button>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Right Column - Assist and Raise a request stacked */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            marginTop: "-60px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "20px",
              gap: "24px",
              height: "50%",
              background:
                isDarkMode === "dark"
                  ? theme.palette.primary.lightBackground
                  : "rgba(38, 198, 249, 0.08)",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "180px",
                maxWidth: "30%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/assist-image.png"
                alt="Assist"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Noto Sans'",
                  fontWeight: 700,
                  fontSize: "18px",
                  lineHeight: "120%",
                  color:
                    theme.palette.cards?.heading || theme.palette.text.primary,
                }}
              >
                Assist - Curated Insights
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Noto Sans'",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "120%",
                  color:
                    theme.palette.cards?.heading || theme.palette.text.primary,
                }}
              >
                Your Smart Assistant to get quick insights. Have you tried
                assist yet?
              </Typography>
              <Button
                variant="contained"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "10px 16px",
                  gap: "8px",
                  width: "fit-content",
                  height: "37px",
                  background:
                    isDarkMode === "dark"
                      ? theme.palette.secondary.main
                      : "#008AAF",
                  borderRadius: "100px",
                  textTransform: "none",
                  "&:hover": {
                    background:
                      isDarkMode === "dark"
                        ? theme.palette.secondary.main
                        : "#008AAF",
                  },
                }}
                onClick={() => navigate("/assist")}
              >
                <Typography
                  sx={{
                    fontFamily: "'Noto Sans'",
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: "120%",
                    color: "#FFFFFF",
                  }}
                >
                  Try Now
                </Typography>
                <NorthEastIcon sx={{ width: 16, height: 16, color: "white" }} />
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "20px",
              gap: "24px",
              height: "50%",
              background:
                isDarkMode === "dark"
                  ? theme.palette.primary.lightBackground
                  : "#FFF3D9",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: "180px",
                maxWidth: "30%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/raise-request-image.png"
                alt="Raise a request"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Noto Sans'",
                  fontWeight: 700,
                  fontSize: "18px",
                  lineHeight: "120%",
                  color:
                    theme.palette.cards?.heading || theme.palette.text.primary,
                }}
              >
                Raise a request
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Noto Sans'",
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "120%",
                  color:
                    theme.palette.cards?.heading || theme.palette.text.primary,
                }}
              >
                Need to request a new data product or add a KPI to an existing
                one?
              </Typography>
              <Button
                variant="contained"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "10px 16px",
                  gap: "8px",
                  width: "fit-content",
                  height: "37px",
                  background:
                    isDarkMode === "dark"
                      ? theme.palette.secondary.main
                      : "#FEC340",
                  borderRadius: "100px",
                  textTransform: "none",
                  "&:hover": {
                    background:
                      isDarkMode === "dark"
                        ? theme.palette.secondary.main
                        : "#FEC340",
                  },
                }}
                onClick={() => navigate("/help")}
              >
                <Typography
                  sx={{
                    fontFamily: "'Noto Sans'",
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: "120%",
                    color: "#FFFFFF",
                  }}
                >
                  Help Center
                </Typography>
                <NorthEastIcon sx={{ width: 16, height: 16, color: "white" }} />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ height: "10%", overflow: "hidden" }}>
        <img
          src={bg}
          alt="Footer background"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>
    </Box>
  );
}

export default HomeScreen;
